import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAccountRoster } from './useAccountRoster';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { profileDisplayLabel } from '@/lib/identity/storeAccount';

/**
 * Resolves `social_posts.authored_by` (the real author of the post — on a
 * shared account `user_id` is always the owner, so `authored_by` is the only
 * column that can name the actual teammate) to a display name, using the
 * account roster that's already fetched and cached by `useAccountRoster` —
 * no per-card query.
 *
 * Object-level "who made this" is only meaningful context on a shared
 * account, and only for the non-owner member's view (an owner already sees
 * everything as theirs; showing "Shared · You" on every card the owner
 * themselves authored would be noise, not signal). So `resolveAuthorLabel`
 * returns `null` — meaning "render nothing" — whenever:
 *   - the viewer is the account owner (solo or owner view: no change), or
 *   - the author id is missing/unresolvable, or
 *   - the author IS the current viewer (no "Shared · You").
 */
export function useTeamAuthorNames() {
  const { user } = useAuth();
  const { data: roster, isLoading } = useAccountRoster();

  const isMember = !(roster?.isOwner ?? true);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!roster) return map;
    if (roster.ownerProfile) {
      map.set(
        roster.ownerProfile.id,
        profileDisplayLabel(roster.ownerProfile, 'Owner', 'Store account'),
      );
    }
    for (const member of roster.members) {
      if (!member.profile) continue;
      map.set(
        member.memberId,
        profileDisplayLabel(member.profile, member.invitedEmail, 'Store account'),
      );
    }
    return map;
  }, [roster]);

  function resolveAuthorLabel(authorId: string | null | undefined): string | null {
    if (!isMember || !authorId || authorId === user?.id) return null;
    return nameById.get(authorId) ?? null;
  }

  return { resolveAuthorLabel, isMember, isLoading };
}

/**
 * Batch-resolves `social_posts.authored_by` (falling back to `user_id` for
 * legacy/pre-migration rows only) for a set of post ids in a single query —
 * used by list/grid views (e.g. `PostsTabList`) whose existing post data
 * (e.g. `SlotPostPreview`) doesn't already carry `authored_by`. Callers
 * should collect every visible post id once per render and pass the whole
 * list here, never call this per-row.
 */
export function usePostAuthorIds(postIds: string[]) {
  const key = postIds.slice().sort().join(',');
  return useQuery({
    queryKey: ['post-author-ids', key],
    queryFn: async (): Promise<Record<string, string>> => {
      if (postIds.length === 0) return {};
      const { data, error } = await supabase
        .from('social_posts')
        .select('id, user_id, authored_by')
        .in('id', postIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        const author = (row.authored_by as string | null) ?? (row.user_id as string | null);
        if (author) map[row.id] = author;
      }
      return map;
    },
    enabled: postIds.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Batch-resolves `social_posts.status` for a set of post ids — sibling to
 * `usePostAuthorIds` above (same rationale: list views like `PostsTabList`
 * work off `SlotPostPreview`, which doesn't carry status). Kept as a
 * separate hook/query rather than folding into `usePostAuthorIds` so that
 * hook's existing `Record<string, string>` (id → authorId) return contract —
 * asserted by `usePostAuthorIds.test.tsx` — stays unchanged for its current
 * callers. Consumer draft-approval (2026-07-15): drives the member-facing
 * "Waiting for approval" chip for a post stuck at `pending_review`.
 */
export function usePostStatusById(postIds: string[]) {
  const key = postIds.slice().sort().join(',');
  return useQuery({
    queryKey: ['post-status-by-id', key],
    queryFn: async (): Promise<Record<string, string>> => {
      if (postIds.length === 0) return {};
      const { data, error } = await supabase
        .from('social_posts')
        .select('id, status')
        .in('id', postIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        if (row.status) map[row.id] = row.status;
      }
      return map;
    },
    enabled: postIds.length > 0,
    staleTime: 30_000,
  });
}
